import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { isNil, noop } from 'lodash';

import { UpgradeTagComponent } from '@shared/upgrade';

type OnChangeFunction = (value: number) => void;
type OnTouchFunction = () => void;

@Component({
  selector: 'la-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatSliderModule, MatFormFieldModule, MatInputModule, UpgradeTagComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderComponent),
      multi: true,
    },
  ],
})
export class SliderComponent implements ControlValueAccessor {
  private cd = inject(ChangeDetectorRef);

  @Input() min?: number;
  @Input() max?: number;
  @Input() step = 1;
  @Input() label = '';
  @Input() disabled = false;
  @Input() suffix?: string;
  @Input() showUpgradeTagWhenDisabled = false;

  sliderValue?: number;

  onChange: OnChangeFunction = noop;
  onTouch: OnTouchFunction = noop;

  registerOnChange(fn: OnChangeFunction): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchFunction): void {
    this.onTouch = fn;
  }

  writeValue(input: number) {
    this.sliderValue = +input;
  }

  inputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = +target.value;
    if (!isNil(value)) {
      this.passValue(value);
    }
  }

  passValue(value: number) {
    value = this.sanitizeValue(value);
    this.sliderValue = value;
    this.onChange(this.sliderValue);
    this.cd.markForCheck();
  }

  private sanitizeValue(value: number): number {
    if (this.min != null && value < this.min) return this.min;
    if (this.max != null && value > this.max) return this.max;
    return value;
  }
}
